import React, { useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface AuthCheckProps {
  onAuthenticated?: () => void;
  autoRefresh?: boolean;
}

/**
 * A component that verifies the user's authentication status
 * and refreshes the token if needed
 */
const AuthCheck: React.FC<AuthCheckProps> = ({ 
  onAuthenticated,
  autoRefresh = true
}) => {
  const { user, signOut } = useAuth();
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const checkAuth = async () => {
    if (!user) {
      setError('You are not signed in. Please sign in to continue.');
      return;
    }

    try {
      setChecking(true);
      setError(null);

      // Check session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        console.error('Session error:', sessionError);
        setError('Authentication error. Please sign in again.');
        return;
      }

      if (!session) {
        setError('Your session has expired. Please sign in again.');
        return;
      }

      // Check if token expires soon (within 5 minutes)
      const expiresAt = session.expires_at ? session.expires_at * 1000 : 0; // convert to ms
      const now = Date.now();
      const fiveMinutes = 5 * 60 * 1000;
      
      if (expiresAt && expiresAt - now < fiveMinutes) {
        if (autoRefresh) {
          console.log('Token expires soon, refreshing...');
          const { data, error: refreshError } = await supabase.auth.refreshSession();
          
          if (refreshError) {
            console.error('Error refreshing token:', refreshError);
            setError('Failed to refresh your session. Please sign in again.');
            return;
          }
          
          if (!data.session) {
            setError('Your session could not be refreshed. Please sign in again.');
            return;
          }
          
          console.log('Session refreshed successfully');
        } else {
          setError('Your session will expire soon. Please refresh your session.');
          return;
        }
      }

      // If we got here, the user is authenticated with a valid token
      if (onAuthenticated) {
        onAuthenticated();
      }
    } catch (error) {
      console.error('Auth check error:', error);
      setError('An error occurred while checking your authentication status.');
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, [user]);

  if (!error) {
    return null;
  }

  return (
    <div className="rounded-md bg-destructive/15 p-4 mb-4">
      <div className="flex items-start gap-4">
        <AlertCircle className="h-5 w-5 text-destructive mt-0.5" />
        <div className="flex-1 space-y-1 text-sm">
          <p className="font-medium text-destructive">{error}</p>
          <div className="flex flex-wrap gap-2 mt-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={checkAuth} 
              disabled={checking}
            >
              Refresh Session
            </Button>
            <Button 
              variant="default" 
              size="sm" 
              onClick={() => signOut()} 
              disabled={checking}
            >
              Sign Out
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthCheck; 